import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Heart, Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLURB = {
  seed: "Buy the servers a coffee.",
  ally: "Help shroud a shelf of books.",
  guardian: "Keep the tool free for a week.",
  patron: "Champion the whole library.",
};

export const Donate = () => {
  const [tiers, setTiers] = useState([]);
  const [count, setCount] = useState(null);
  const [displayCount, setDisplayCount] = useState(0);
  const [visible, setVisible] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const [loadingTier, setLoadingTier] = useState(null);
  const [thanks, setThanks] = useState(false);
  const [polling, setPolling] = useState(false);
  const counterRef = useRef(null);
  const animatedRef = useRef(false);

  useEffect(() => {
    axios.get(`${API}/donations/tiers`).then(({ data }) => setTiers(data.tiers)).catch(() => {});
    axios.get(`${API}/stats`).then(({ data }) => setCount(data.documents_protected)).catch(() => {});
  }, []);

  useEffect(() => {
    const el = counterRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || count === null || animatedRef.current) return;
    animatedRef.current = true;
    const duration = 1400;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setDisplayCount(Math.round(eased * count));
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [visible, count]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (params.get("donation") === "cancelled") {
      toast("Donation cancelled — no charge was made.");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    if (!sessionId) return;

    setPolling(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/donations/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPolling(false); setThanks(true);
          toast.success("Thank you for supporting Inkshroud!");
          window.history.replaceState({}, "", window.location.pathname);
          setTimeout(() => document.getElementById("donate")?.scrollIntoView({ behavior: "smooth" }), 300);
          return;
        }
        if (data.status === "expired" || attempts >= 6) {
          setPolling(false);
          if (data.status === "expired") toast.error("Donation session expired.");
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setPolling(false);
        window.history.replaceState({}, "", window.location.pathname);
      }
    };
    poll();
  }, []);

  const donate = async (tierId, amount) => {
    setLoadingTier(tierId);
    try {
      const payload = { tier_id: tierId, origin_url: window.location.origin };
      if (tierId === "custom") payload.custom_amount = amount;
      const { data } = await axios.post(`${API}/donations/checkout`, payload);
      // Stripe checkout cannot render inside an iframe (X-Frame-Options).
      // When embedded, open it in a new top-level tab instead.
      if (window.self !== window.top) {
        window.open(data.url, "_blank", "noopener");
        setLoadingTier(null);
      } else {
        window.location.href = data.url;
      }
    } catch (e) {
      setLoadingTier(null);
      toast.error("Could not start donation", { description: e.response?.data?.detail || "Please try again." });
    }
  };

  const submitCustom = () => {
    const amt = parseFloat(customAmount);
    if (!amt || amt < 1 || amt > 10000) {
      toast.error("Enter an amount", { description: "Between $1 and $10,000." });
      return;
    }
    donate("custom", Math.round(amt * 100) / 100);
  };

  return (
    <section id="donate" className="relative max-w-6xl mx-auto px-6 py-20 md:py-28 border-t border-[#485354]/25 scroll-mt-20" data-testid="donate-section">
      <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#70250a] mb-4">Keep it free</p>
      <h2 className="font-cinzel font-bold text-3xl sm:text-4xl text-[#E6E2D6] uppercase tracking-tight mb-4">Support the project</h2>
      <p className="font-lora text-[#E6E2D6]/60 max-w-2xl mb-12">
        Inkshroud is free for every author. Donations cover the servers and fund new defenses so we can protect more original works from the machines.
      </p>

      <div ref={counterRef} className="mb-14 border border-[#6d411c]/40 bg-[#0a2632]/50 px-8 py-8 flex flex-col sm:flex-row items-center gap-4 sm:gap-8" data-testid="documents-protected-counter">
        <span className="font-cinzel font-black text-5xl sm:text-6xl text-[#70250a] tabular-nums" data-testid="documents-protected-count">
          {count === null ? "—" : displayCount.toLocaleString()}
        </span>
        <div>
          <p className="font-cinzel text-xl text-[#E6E2D6] uppercase tracking-wide">Documents protected</p>
          <p className="font-lora text-sm text-[#E6E2D6]/60">and counting — every donation shrouds more.</p>
        </div>
      </div>

      {thanks && (
        <div className="mb-10 border border-[#6d411c]/50 bg-[#0a2632]/60 p-6 flex items-center gap-4" data-testid="donate-thanks">
          <Check className="w-6 h-6 text-[#6d411c] shrink-0" />
          <p className="font-lora text-[#E6E2D6]/85">Your donation came through — thank you for helping guard the craft.</p>
        </div>
      )}

      {polling && (
        <div className="mb-10 border border-[#485354]/50 bg-[#041b24]/60 p-6 flex items-center gap-4" data-testid="donate-polling">
          <Loader2 className="w-5 h-5 text-[#6d411c] animate-spin" />
          <p className="font-mono text-sm text-[#E6E2D6]/70">Confirming your donation…</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {tiers.map((t) => (
          <div key={t.id} className="border border-[#485354]/40 bg-[#041b24]/60 p-7 flex flex-col hover:-translate-y-1 hover:border-[#6d411c]/60 transition-transform transition-colors duration-300" data-testid={`donate-tier-${t.id}`}>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#485354] mb-4">{t.label}</p>
            <p className="font-cinzel text-4xl text-[#E6E2D6] mb-3">${t.amount % 1 === 0 ? t.amount : t.amount.toFixed(2)}</p>
            <p className="font-lora text-sm text-[#E6E2D6]/60 leading-relaxed mb-7 flex-1">{BLURB[t.id] || "Support free protection."}</p>
            <button
              data-testid={`donate-btn-${t.id}`}
              disabled={loadingTier !== null}
              onClick={() => donate(t.id)}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#70250a] text-[#E6E2D6] font-cinzel font-bold uppercase tracking-widest text-xs py-4 hover:bg-[#8A2E0D] transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loadingTier === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
              Donate
            </button>
          </div>
        ))}
      </div>

      <div className="mt-6 border border-[#485354]/40 bg-[#041b24]/60 p-7 flex flex-col sm:flex-row sm:items-end gap-5" data-testid="donate-custom">
        <div className="flex-1">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#485354] mb-3">Name your own amount</p>
          <div className="flex items-center border border-[#485354]/60 focus-within:border-[#70250a] transition-colors bg-[#020f14]">
            <span className="font-cinzel text-2xl text-[#6d411c] pl-4 pr-1">$</span>
            <input
              type="number" min="1" max="10000" step="1" inputMode="decimal"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitCustom(); }}
              placeholder="50"
              data-testid="donate-custom-input"
              className="w-full bg-transparent text-[#E6E2D6] font-cinzel text-2xl py-3 pr-4 outline-none placeholder:text-[#485354]/50"
            />
          </div>
        </div>
        <button
          data-testid="donate-custom-btn"
          disabled={loadingTier !== null}
          onClick={submitCustom}
          className="inline-flex items-center justify-center gap-2 bg-[#70250a] text-[#E6E2D6] font-cinzel font-bold uppercase tracking-widest text-xs py-4 px-8 hover:bg-[#8A2E0D] transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {loadingTier === "custom" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
          Donate custom
        </button>
      </div>

      <p className="font-mono text-[10px] uppercase tracking-widest text-[#485354]/70 mt-8">Secure one-time payment via Stripe · no account required</p>
    </section>
  );
};
